import { Component, DestroyRef, Input, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

import { UserContextService } from '@app/modules/core/services/user-context.service';

import {
  cancelChangeWhatsAppField,
  cancelEnterCode,
  processAuthCodeForWhatsApp,
  selectError,
  selectIsLoading,
  selectShowEnterCode,
  sendOneTimeAuthWhatsApp,
  selectWhatsAppField
} from '../../store';

@Component({
  selector: 'topo-register-whatsapp',
  templateUrl: './register-whatsapp.component.html',
  styleUrls: ['./register-whatsapp.component.scss'],
  standalone: false
})
export class RegisterWhatsAppComponent {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private userContext = inject(UserContextService);
  private destroyRef = inject(DestroyRef);

  @Input() isDesktop: boolean;

  form = this.fb.group({
    phone: [null, Validators.required],
    code: [null, Validators.required]
  });

  blurredOnce = false;
  isLoading = this.store.selectSignal(selectIsLoading);

  whatsAppField = this.store.selectSignal(selectWhatsAppField);

  enterCode$ = this.store.select(selectShowEnterCode);

  err$ = this.store.selectSignal(selectError);
  isInvalidPhone = signal(false);
  error = computed(() => {
    const err = this.err$();

    const isInvalidPhone = this.isInvalidPhone();

    if (isInvalidPhone) {
      return 'NOTIFICATION-PREFERENCES.ERROR-ENTER-VALID-PHONE';
    }

    return !!err
      ? err?.message?.indexOf('NotAuthorizedException') > 0
        ? 'NOTIFICATION-PREFERENCES.ERROR-INVALID-CODE'
        : 'ERRORS.GENERAL'
      : null;
  });

  get phoneControl(): FormControl {
    return this.form.get('phone') as FormControl;
  }

  ngOnInit(): void {
    this.phoneControl.statusChanges
      .pipe(
        filter(() => this.phoneControl.dirty && this.blurredOnce),
        debounceTime(10),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isInvalidPhone.set(this.phoneControl.invalid);
      });
  }

  requestCodeClick(): void {
    this.isInvalidPhone.set(this.phoneControl.invalid);

    if (this.phoneControl.valid) {
      const { cultureCode, organizationCode: orgCode } = this.userContext.getContext();

      this.store.dispatch(
        sendOneTimeAuthWhatsApp({
          orgCode,
          phone: this.phoneControl.value,
          cultureCode,
          recaptchaToken: null
        })
      );
    }
  }

  cancelWhatsApp(): void {
    this.store.dispatch(cancelEnterCode());
    this.store.dispatch(cancelChangeWhatsAppField());
  }

  verifyCode(): void {
    const { invalid, value } = this.form;

    if (invalid) {
      return;
    }

    this.store.dispatch(
      processAuthCodeForWhatsApp({
        orgCode: this.userContext.getContext().organizationCode,
        code: value.code,
        userKey: value.phone,
        fieldName: this.whatsAppField()
      })
    );
  }

  handleBlur(): void {
    const { dirty, touched, invalid } = this.phoneControl;

    if (dirty && touched) {
      this.isInvalidPhone.set(invalid);

      if (!this.blurredOnce) {
        this.blurredOnce = true;
      }
    }
  }
}
